'use client'

import { Cell, Divider, Label, Loader, Spacer, Text } from '@vinted/web-ui'
import classNames from 'classnames'
import { useEffect, useState } from 'react'

import { HelpCenterTransactionItem } from '@marketplace-web/help-center/help-center-transactions-feature'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import {
  useBrowserNavigation,
  navigateToPage,
} from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { urlWithParams } from '@marketplace-web/shared/utils'
import { HelpCenterFaqContent } from '@marketplace-web/help-center/help-center-faq-content-feature'
import {
  LiveChatStatus,
  getLiveChatAvailability,
  liveChatBannerClickedEvent,
  liveChatBannerViewedEvent,
  LiveChatBannerViewStatus,
} from '@marketplace-web/help-center/help-center-live-chat-data'
import useHelpCenterSessionTracking from '@marketplace-web/help-center/help-center-session-tracking-util'

import { HelpCenterNavigation } from '@marketplace-web/help-center/help-center-faq-navigation-feature'
import { FaqEntryType } from '@marketplace-web/help-center/help-center-faq-entry-url-data'

import {
  getFaqEntries,
  transformHelpCenterFaqEntryRubyDto,
  HelpCenterQuestionModel as HelpCenterFaqQuestionModel,
  HelpCenterFaqEntryRubyDto,
  FaqEntryModel,
} from '@marketplace-web/help-center/help-center-faq-entry-data'
import { getIsNewComplaintViewEnabled } from '@marketplace-web/complaints/complaints-data'
import {
  getRecentTransactions,
  transformHelpCenterTransactionsResponse,
  HelpCenterTransactionModel,
} from '@marketplace-web/help-center/help-center-transactions-data'
import { transformHelpCenterNavigationResponse } from '@marketplace-web/help-center/help-center-home-data'

import { HelpCenterSearchInputNew } from '@marketplace-web/help-center/help-center-ai-powered-help-feature'

import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import HelpCenterFaqFeedbackForm from '../HelpCenterFaqFeedbackForm'
import HelpCenterFaqLinks from '../HelpCenterFaqLinks'
import HelpCenterFaqFeedbackFormLink from '../HelpCenterFaqFeedbackFormLink'
import HelpCenterContactLink from '../HelpCenterContactLink'
import HelpCenterInfoBox from '../HelpCenterInfoBox'
import HelpCenterRecommendedFaqList from '../HelpCenterRecommendedFaqList'
import HelpCenterSearchEmptyState from '../HelpCenterSearchEmptyState'
import HelpCenterRelatedQuestions from '../HelpCenterRelatedQuestions'
import { APP_FEEDBACKS_URL, LIVE_CHAT_FORM_URL } from '../constants/routes'

type Props = {
  faqEntryDto: HelpCenterFaqEntryRubyDto | null
  faqQuestions: Array<HelpCenterFaqQuestionModel> | null
  faqRecommendedQuestions?: Array<HelpCenterFaqQuestionModel> | null
  faqRelatedQuestions?: Array<HelpCenterFaqQuestionModel> | null
  channel: string
  transaction: HelpCenterTransactionModel | null
  faqEntryBody: string | null
  infoBoxBody: string | null
  answerNotFoundId: number | null
  transformedFaqEntry?: FaqEntryModel
  faqAccessChannel?: string
  faqEntityId?: number
}

type LiveChatAvailability = {
  status: LiveChatStatus
  reason: string
  availability_information: string
}

const HelpCenterFaqEntry = ({
  faqEntryDto,
  faqQuestions,
  faqRecommendedQuestions,
  faqRelatedQuestions,
  channel,
  transaction,
  faqEntryBody,
  infoBoxBody,
  answerNotFoundId,
  transformedFaqEntry,
  faqAccessChannel,
  faqEntityId,
}: Props) => {
  const faqEntry = faqEntryDto
    ? transformHelpCenterFaqEntryRubyDto(faqEntryDto)
    : transformedFaqEntry || null

  const { user } = useSession()
  const isLoggedIn = Boolean(user)

  const isTransactionRequired = faqEntryDto
    ? faqEntryDto.transaction_required
    : transformedFaqEntry?.transactionIdRequired

  const isContentOnly = useSession().isContentOnlyView

  const translate = useTranslate('help_center')
  const translateLiveChat = useTranslate('live_chat')

  const {
    fetch: fetchFaqEntries,
    isLoading: isNavigationFaqEntriesLoading,
    transformedData: transformedFaqEntries,
  } = useFetch(getFaqEntries, transformHelpCenterNavigationResponse)

  const {
    fetch: fetchRecentTransactions,
    transformedData: recentTransactions,
    isLoading: isTransactionsLoading,
  } = useFetch(getRecentTransactions, transformHelpCenterTransactionsResponse)

  const { fetch: fetchIsNewComplaintView, transformedData: isNewComplaintReportView } = useFetch(
    getIsNewComplaintViewEnabled,
    response => response.complaint_view_v2,
  )

  const { show_form, transaction_id, search_text } = useBrowserNavigation().searchParams
  const { track } = useTracking()
  const { helpCenterSessionId } = useHelpCenterSessionTracking()
  useEffect(() => {
    fetchFaqEntries()
  }, [fetchFaqEntries])

  useEffect(() => {
    if (!isTransactionRequired) {
      return
    }

    fetchRecentTransactions(1)
  }, [fetchRecentTransactions, isTransactionRequired])

  const [liveChatAvailability, setLiveChatAvailability] = useState<LiveChatAvailability | null>(
    null,
  )
  const isLiveChatFeatureEnabled = useFeatureSwitch('live_chat_web')

  useEffect(() => {
    const { status } = liveChatAvailability || {}
    if (!status) return

    const viewStatus =
      status === LiveChatStatus.Available
        ? LiveChatBannerViewStatus.Available
        : LiveChatBannerViewStatus.Unavailable

    track(liveChatBannerViewedEvent({ status: viewStatus, helpCenterSessionId }))
  }, [helpCenterSessionId, liveChatAvailability, track])

  useEffect(() => {
    const fetchLiveChatAvailability = async () => {
      if (!transaction?.id) return

      const response = await getLiveChatAvailability(transaction?.id)

      if ('errors' in response) return

      setLiveChatAvailability(response)
    }
    fetchLiveChatAvailability()
  }, [transaction?.id])

  useEffect(() => {
    if (!faqEntityId || !faqEntry?.isContactAllowed) return

    fetchIsNewComplaintView(faqEntityId)
  }, [fetchIsNewComplaintView, faqEntityId, faqEntry])

  const isAppFeedbackButtonVisible = faqEntry?.id === FaqEntryType.MembersFeedback

  const getTransactionId = () => {
    if (transaction?.id) {
      return transaction.id
    }

    // use the URL transaction_id parameter if transaction is not available
    const transactionId = Number(transaction_id)

    if (transactionId > 0) {
      return transactionId
    }

    return undefined
  }

  const renderSidebarNavigation = () => {
    if (isContentOnly) {
      return null
    }

    return (
      <aside className="body-content__sidebar faq__navigation faq__navigation--secondary">
        <HelpCenterNavigation
          navigationFaqEntries={transformedFaqEntries}
          isLoading={isNavigationFaqEntriesLoading}
        />
      </aside>
    )
  }

  const renderSearch = () => {
    if (isContentOnly || !transaction) {
      return null
    }

    return (
      <div className="faq__ai-search-input-wrapper">
        <HelpCenterSearchInputNew />
      </div>
    )
  }

  const renderRecommendedFaqs = () => {
    if (isContentOnly || transaction || !faqQuestions) {
      return null
    }

    return (
      <>
        {faqEntry?.title ? (
          <Cell title={<Text as="h1" text={faqEntry.title} type={Text.Type.Heading} />} />
        ) : null}
        {faqRecommendedQuestions && faqRecommendedQuestions.length ? (
          <HelpCenterRecommendedFaqList faqEntries={faqRecommendedQuestions} />
        ) : null}
        <Cell styling={Cell.Styling.Tight}>
          <Label type={Label.Type.Stacked} text={translate('faq.pick_a_topic')} />
          <Spacer />
        </Cell>
      </>
    )
  }

  const renderTransaction = () => {
    if (!transaction) {
      return null
    }

    return (
      <>
        <Spacer orientation={Spacer.Orientation.Horizontal} size={Spacer.Size.Large} />
        <HelpCenterTransactionItem transaction={transaction} />
        {liveChatAvailability?.status === LiveChatStatus.Ineligible && <Divider />}
      </>
    )
  }

  const renderLiveChatAvailabilityBanner = () => {
    const { status, reason } = liveChatAvailability || {}
    const transactionId = getTransactionId()
    // veirfy both transaction and transactionId to avoid showing the banner when transaction is not available inside the faq
    const isEligibleForLiveChat =
      status &&
      status !== LiveChatStatus.Ineligible &&
      transaction &&
      transactionId &&
      isLiveChatFeatureEnabled

    if (!isEligibleForLiveChat) {
      return null
    }

    const titleKey =
      status === LiveChatStatus.Available ? 'banner.title' : 'banner.unavailable_title'

    const onClickHandler = () => {
      if (status === LiveChatStatus.Unavailable) return
      track(liveChatBannerClickedEvent({ helpCenterSessionId }))
      navigateToPage(urlWithParams(LIVE_CHAT_FORM_URL(transactionId), { ch: channel }))
    }
    const isLiveChatAvailable = status === LiveChatStatus.Available

    return (
      <div className="live-chat-banner u-text-wrap">
        <Cell
          theme={isLiveChatAvailable ? 'successLightExperimental' : 'inverseExperimental'}
          testId="live-chat-banner"
          body={<Text as="h4" text={reason} type={Text.Type.Caption} />}
          chevron
          title={translateLiveChat(titleKey)}
          type={isLiveChatAvailable ? Cell.Type.Navigating : undefined}
          onClick={onClickHandler}
          clickable={isLiveChatAvailable}
        />
        <Spacer size={Spacer.Size.XLarge} />
      </div>
    )
  }

  const renderFaqEntryFeedbackForm = () => {
    if (show_form) {
      return null
    }

    if (isAppFeedbackButtonVisible) {
      return (
        <>
          <HelpCenterFaqFeedbackFormLink feedbacks_path={APP_FEEDBACKS_URL} />
          <Spacer size={Spacer.Size.XLarge} />
        </>
      )
    }

    if (!isAppFeedbackButtonVisible && !isContentOnly && faqEntry) {
      return (
        <>
          <Divider />
          <Spacer size={Spacer.Size.XLarge} />
          <HelpCenterFaqFeedbackForm
            faqId={faqEntry.id}
            faqEntryChannel={channel}
            faqAccessChannel={faqAccessChannel}
          />
          <Spacer size={Spacer.Size.XLarge} />
        </>
      )
    }

    return null
  }

  const renderFaqEntryRelatedQuestions = () => {
    if (!faqRelatedQuestions || !faqRelatedQuestions.length) {
      return null
    }

    return (
      <>
        <Divider />
        <Spacer size={Spacer.Size.XLarge} />
        <HelpCenterRelatedQuestions
          questions={faqRelatedQuestions}
          faqAccessChannel={faqAccessChannel}
          faqEntryChannel={channel}
          faqEntityId={faqEntityId}
        />
        <Spacer size={Spacer.Size.XLarge} />
      </>
    )
  }

  const renderFaqEntryContactLink = () => {
    const isMissingRequiredTransactions =
      isTransactionRequired && (!recentTransactions || recentTransactions.totalCount === 0)
    if (
      show_form ||
      isAppFeedbackButtonVisible ||
      !faqEntry ||
      isMissingRequiredTransactions ||
      !faqEntry.isContactAllowed
    ) {
      return null
    }

    if (!faqEntry.isAnonymousAllowed && faqEntry.isContactAllowed && !isLoggedIn) {
      return null
    }

    return (
      <>
        <Divider />
        <HelpCenterContactLink
          faqEntryId={faqEntry.id}
          transactionId={getTransactionId()}
          proceedButton={faqEntry.proceedButton}
          isComplaintsFaq={faqEntry.isComplaintsFaq}
          isNewComplaintReportView={isNewComplaintReportView}
        />
      </>
    )
  }

  const renderFaqEntryFooter = () => (
    <>
      <Spacer size={Spacer.Size.XLarge} />
      {renderFaqEntryFeedbackForm()}
      {renderFaqEntryRelatedQuestions()}
      {renderFaqEntryContactLink()}
    </>
  )

  const renderFaqEntryContent = () => {
    if (isTransactionsLoading) {
      return (
        <div
          data-testid="transactions-loader"
          className="u-flexbox u-justify-content-center u-align-items-center"
        >
          <Loader size={Loader.Size.XLarge} />
        </div>
      )
    }

    if (faqQuestions) {
      return <HelpCenterFaqLinks questions={faqQuestions} />
    }

    return (
      <>
        {infoBoxBody ? <HelpCenterInfoBox body={infoBoxBody} /> : null}
        <Cell styling={Cell.Styling.Wide}>
          {faqEntry && (
            <HelpCenterFaqContent
              title={faqEntry.title}
              content={faqEntryBody}
              faqEntryId={faqEntry.id}
            />
          )}
          {renderFaqEntryFooter()}
        </Cell>
      </>
    )
  }

  const aiPoweredHelpWebAbTest = useAbTest('ai_help_chatbot_v2')
  const isAiPoweredHelpFeatureEnabled = useFeatureSwitch('help_center_ai_powered_help')

  const isAiPoweredHelpEnabled =
    aiPoweredHelpWebAbTest?.variant === 'on' && isAiPoweredHelpFeatureEnabled

  const isOffVariantStylingApplied = !isAiPoweredHelpEnabled && !!search_text

  const faqContainerClass = classNames('faq', {
    push2: isContentOnly,
    'u-padding-none': isContentOnly,
  })

  return (
    <div className={isOffVariantStylingApplied ? 'faq__search-results-container' : ''}>
      {renderSidebarNavigation()}
      {/*
       * Element ID on this div is necessary for mobile apps to calculate document height when opened in webview.
       * Please DO NOT remove or change the ID without syncing with mobile apps first.
       * ID is conditionally added for old rails version as temporary solution.
       */}
      <div
        id={faqEntryDto ? 'faq-container' : ''}
        className={faqContainerClass}
        data-testid="faq-container"
      >
        {renderSearch()}
        {renderRecommendedFaqs()}
        {answerNotFoundId && !faqQuestions ? (
          <HelpCenterSearchEmptyState channel={channel} answerNotFoundId={answerNotFoundId} />
        ) : (
          <>
            {renderTransaction()}
            {renderLiveChatAvailabilityBanner()}
            {renderFaqEntryContent()}
          </>
        )}
      </div>
    </div>
  )
}

export default HelpCenterFaqEntry
